import tealium from '../../tealium';
import { registerAnalyticsEventHandler } from '../registry';
import { IDataLayer } from '../../../types';

type ILPEventData = {
  engagementType: number;
};

const defaultData: IDataLayer = {
  liveChatManualInitiated: {
    event_name: 'live_chat_user_initiated',
    event_category: 'live chat interaction',
    event_action: 'user initiated',
    event_label: null, // <page_name>
    event_value: '0',
    event_noninteraction: 'false',
    live_chat_initiation_type: 'user initiated',
  },

  liveChatManualPreSurvey: {
    event_name: 'live_chat_manual_prechat_survey',
    event_category: 'live chat interaction',
    event_action: 'prechat survey',
    event_label: 'shown',
    live_chat_initiation_type: 'user initiated',
  },

  liveChatManualWaiting: {
    event_name: 'live_chat_manual_prechat_accepted',
    event_category: 'live chat interaction',
    event_action: 'prechat survey',
    event_label: 'accepted',
    live_chat_initiation_type: 'user initiated',
    live_chat_type: 'live chat',
  },

  liveChatManualChatInitialize: {
    event_name: 'live_chat_manual_chat_initialize',
    event_category: 'live chat interaction',
    event_action: 'chat start',
    event_label: 'live chat',
    live_chat_initiation_type: 'user initiated',
    live_chat_type: 'live chat',
  },

  liveChatManualChatended: {
    event_name: 'live_chat_manual_chat_ended',
    event_category: 'live chat interaction',
    event_action: 'chat ended',
    event_label: 'live chat',
    live_chat_initiation_type: 'user initiated',
    live_chat_type: 'live chat',
  },

  liveChatManualChatting: {
    event_name: 'live_chat_manual_chatting',
    event_category: 'live chat interaction',
    event_action: 'chatting',
    event_label: 'live chat',
    live_chat_initiation_type: 'user initiated',
    live_chat_type: 'live chat',
  },

  liveChatProactiveDisplayed: {
    event_name: 'live_chat_proactive_shown',
    event_category: 'live chat interaction',
    event_action: 'proactive chat',
    event_label: 'shown',
    event_value: '0',
    event_noninteraction: 'false',
  },

  liveChatProactiveInitiated: {
    event_name: 'live_chat_proactive_accepted',
    event_category: 'live chat interaction',
    event_action: 'proactive chat',
    event_label: 'accepted',
    event_value: '0',
    event_noninteraction: 'false',
    live_chat_initiation_type: 'proactive',
  },

  liveChatProactivePreSurvey: {
    event_name: 'live_chat_proactive_prechat_survey',
    event_category: 'live chat interaction',
    event_label: 'shown',
    event_action: 'prechat survey',
    live_chat: 'Pre Survey Chat',
    live_chat_initiation_type: 'proactive',
  },

  liveChatProactiveWaiting: {
    event_name: 'live_chat_proactive_prechat_accepted',
    event_category: 'live chat interaction',
    event_action: 'prechat survey',
    event_label: 'accepted',
    live_chat_initiation_type: 'proactive',
    live_chat_type: 'live chat',
  },

  liveChatProactiveChatting: {
    event_name: 'live_chat_proactive_chatting',
    event_category: 'live chat interaction',
    event_action: 'chat start',
    event_label: 'live chat',
    live_chat_initiation_type: 'proactive',
    live_chat_type: 'live chat',
  },

  liveChatContentCardClick: {
    event_name: 'content_module_click',
    enh_action: 'promo_click',
    event_category: 'ecommerce',
    event_action: 'promotion click',
    event_label: null, //"banner click | ['" + image + "']"
    promo_pos: null,
    promo_creative: ['liveperson content card'],
    promo_name: null,
    promo_id: null,
  },

  liveChatProdClick: {
    event_name: 'liveperson',
    event_category: 'liveperson_text_chat',
    event_action: 'product_click',
    event_label: 'product_click',
    enh_action: 'product_click',
    product_position: '1',
    product_list: ['liveperson_livechat'],
    product_id: null,
  },

  liveChatAddtoBag: {
    event_name: 'liveperson',
    event_category: 'liveperson_text_chat',
    event_action: 'product_details',
    event_label: 'view',
    enh_action: 'detail',
    product_id: null,
  },

  liveChatImpression: {
    event_name: 'liveperson',
    event_category: 'liveperson_text_chat',
    event_action: 'product_impression',
    event_label: null, // prodid
    product_impression_id: null, // prodid
    product_impression_list: ['liveperson_livechat'],
    event_noninteraction: 'true',
  },

  liveChatContentCardImpression: {
    enh_action: 'promo_click',
    event_name: 'content_module_click',
    event_category: 'ecommerce',
    event_action: 'promotion click',
    event_label: null, // prodid
    promo_pos: null,
    promo_creative: ['liveperson_content_card'],
    promo_name: null,
    promo_id: null,
  },
};

const EVENT_TYPES = {
  IMPRESSION: 'IMPRESSION',
  CLICK: 'CLICK',
};

const CARD_TYPES = {
  'image-only-card': 'Image only card',
  'full-content-card': 'Full content card',
  'double-button-image-card': 'Double button image card',
  'product-card': 'Product card',
};

const PRODUCT_CARD_DATA_TYPES = {
  details: 'DETAILS',
  'add-to-cart': 'ADD TO CART',
};

function trackLiveChat(
  this: string,
  eventName: string,
  eventData: IDataLayer = {},
) {
  const data = tealium.dataLayer?.data || {};
  if (eventName.match(/Initiated/)) {
    eventData.event_label = tealium.dataLayer?.data?.page_name || '';
  }

  // Load the tracking after we launch LivePerson from Tealium
  let liveChatTrackingLoaded = false;
  if (liveChatTrackingLoaded === false) {
    let chatEngagementType = 0;
    if (window?.lpTag?.events) {
      //Livechat Engagements are displayed on the page
      liveChatTrackingLoaded = true;
      window.lpTag.events.bind({
        eventName: 'OFFER_DISPLAY',
        func: function (eventData: ILPEventData) {
          if (eventData.engagementType === 1) {
            eventName = 'liveChatProactiveDisplayed';
            tealium.evtLink({
              ...data,
              ...defaultData[eventName],
              ...eventData,
            });
          }
        },
      });

      // Livechat was clicked on the page
      window.lpTag.events.bind({
        eventName: 'OFFER_CLICK',
        func: function (eventData: ILPEventData) {
          chatEngagementType = eventData.engagementType;
          if (eventData.engagementType === 1) {
            eventName = 'liveChatProactiveInitiated';
            tealium.evtLink({
              ...data,
              ...defaultData[eventName],
              ...eventData,
            });
          } else {
            // otherwise it was manual
            eventName = 'liveChatManualInitiated';
            tealium.evtLink({
              ...data,
              ...defaultData[eventName],
              ...eventData,
            });
          }
        },
      });

      // Livechat conversationInfo Event start.
      window.lpTag.events.bind({
        eventName: 'conversationInfo',
        func: function (eventData: { skill: string; visitorId: string }) {
          const engagementType =
            chatEngagementType === 1 ? 'Proactive' : 'Manual';
          if (eventData.skill && eventData.visitorId) {
            eventName = 'liveChat' + engagementType + 'Chatting';
            tealium.evtLink({
              ...data,
              ...defaultData[eventName],
              ...eventData,
            });
          }
        },
      });
      // Livechat conversationInfo Event end.

      // Livechat state change.
      let chatendEvents: string | number | NodeJS.Timeout | undefined;
      window.lpTag.events.bind({
        eventName: 'state',
        func: function (eventData: { state: string }) {
          const engagementType: string =
            chatEngagementType === 1 ? 'Proactive' : 'Manual';
          if (eventData.state === 'ended') {
            clearTimeout(chatendEvents);
            chatendEvents = setTimeout(function () {
              eventName = 'liveChat' + engagementType + 'Chatended';
              tealium.evtLink({
                ...data,
                ...defaultData[eventName],
                ...eventData,
              });
            }, 2000);
          }
        },
      });

      window.lpTag.events.bind({
        eventName: 'state',
        func: function (eventData: { state: string }) {
          let engagementType = '';
          if (chatEngagementType === 1) {
            engagementType = 'Proactive';
          } else {
            engagementType = 'Manual';
          }
          if (eventData.state === 'preChat') {
            // When the survey before the chat is shown
            eventName = 'liveChat' + engagementType + 'PreSurvey';
            tealium.evtLink({
              ...data,
              ...defaultData[eventName],
              ...eventData,
            });
          }
          if (eventData.state === 'waiting') {
            // When the user submits the survey
            eventName = 'liveChat' + engagementType + 'Waiting';
            tealium.evtLink({
              ...data,
              ...defaultData[eventName],
              ...eventData,
            });
          }
          if (eventData.state === 'init') {
            // When the user start the chat
            eventName = 'liveChat' + engagementType + 'ChatInitialize';
            tealium.evtLink({
              ...data,
              ...defaultData[eventName],
              ...eventData,
            });
          }
        },
      });
    }
  }

  // Product card & contend card
  let loaded = false;

  if (window.lpTag && window.lpTag.hooks) {
    window.lpTag.hooks.push({
      name: 'AFTER_GET_LINES',
      callback: function (data: IDataLayer) {
        if (data.data.lines.length) {
          data.data.lines.forEach(function (el: IDataLayer) {
            if (el.type === 'richContent') {
              let metadata;
              if (el.metadata && el.metadata.length) {
                metadata = el.metadata[0];
              }

              // Installing click handlers
              if (el) setListener(el.localId, metadata);
            }
          });

          if (loaded && data.data.lines[data.data.lines.length - 1]) {
            // New card acquisition
            cardImpression(data.data.lines[data.data.lines.length - 1]);
          }
        }

        loaded = true;
      },
    });
  }
}

function setListener(id: string, metadata: string) {
  const selector = '#lp_line_bubble_' + id + ' .lp-json-pollock';
  const element = document.querySelector(selector);
  if (element) {
    element.addEventListener('click', clickHandler.bind(null, metadata));
  }
}

// Click event handling on card
function clickHandler(metadata: string, e: IDataLayer) {
  const target = e.target;
  const targetNodeName = target.nodeName;
  if (
    targetNodeName === 'BUTTON' &&
    JSON.parse(metadata).type === CARD_TYPES['product-card']
  ) {
    getAnalyticTemplates(metadata, EVENT_TYPES.CLICK, target.innerText);
  }
  if (
    targetNodeName === 'BUTTON' &&
    JSON.parse(metadata).type === CARD_TYPES['full-content-card']
  ) {
    getAnalyticTemplates(metadata, EVENT_TYPES.CLICK, target.innerText);
  }
}

// New card acquisition event handler.
function cardImpression(dataList: IDataLayer) {
  if (
    dataList &&
    dataList.type === 'richContent' &&
    dataList.metadata &&
    dataList.metadata.length > 0
  ) {
    getAnalyticTemplates(dataList.metadata, EVENT_TYPES.IMPRESSION, '');
  }
}

// Defining templates based on metadata, eventType and dataType
function getAnalyticTemplates(
  metadata: string,
  eventType: string,
  dataType: string | undefined,
  eventData: IDataLayer = {},
) {
  const data = JSON.parse(metadata);
  const cardType = JSON.parse(metadata).type;
  const promoId = cardType + ' - liveperson content card - ';
  let image;

  //if (data.productId) prodId = 'PROD' + data.productId;
  if (data.imageUrl) {
    image = data.imageUrl.split('/');
    image = image[image.length - 1];
  }

  if (
    cardType === CARD_TYPES['full-content-card'] ||
    cardType === CARD_TYPES['double-button-image-card'] ||
    cardType === CARD_TYPES['image-only-card']
  ) {
    if (eventType === EVENT_TYPES.IMPRESSION) {
      eventData.promo_pos = [cardType];
      (eventData.promo_creative = ['liveperson content card']),
        (eventData.promo_name = [image]),
        (eventData.promo_id = [promoId + image]),
        tealium.evtLink({
          ...data,
          ...defaultData['liveChatContentCardImpression'],
          ...eventData,
        });
    }

    if (eventType === EVENT_TYPES.CLICK) {
      eventData.event_label = 'banner click';
      eventData.promo_pos = [cardType];
      (eventData.promo_creative = ['liveperson content card']),
        (eventData.promo_name = [image]),
        (eventData.promo_id = [promoId + image]),
        tealium.evtLink({
          ...data,
          ...defaultData['liveChatContentCardClick'],
          ...eventData,
        });
    }
  }

  if (cardType === CARD_TYPES['product-card']) {
    if (eventType === EVENT_TYPES.CLICK) {
      if (dataType === PRODUCT_CARD_DATA_TYPES['details']) {
        eventData.product_id = ['PROD' + data.productId];
        tealium.evtLink({
          ...data,
          ...defaultData['liveChatProdClick'],
          ...eventData,
        });
      }
      if (dataType === PRODUCT_CARD_DATA_TYPES['add-to-cart']) {
        eventData.product_id = ['PROD' + data.productId];
        tealium.evtLink({
          ...data,
          ...defaultData['liveChatProdClick'],
          ...eventData,
        });
        tealium.evtLink({
          ...data,
          ...defaultData['liveChatAddtoBag'],
          ...eventData,
        });
      }
    }
    if (eventType === EVENT_TYPES.IMPRESSION) {
      eventData.event_label = ['PROD' + data.productId];
      eventData.product_impression_id = ['PROD' + data.productId];
      tealium.evtLink({
        ...data,
        ...defaultData['liveChatImpression'],
        ...eventData,
      });
    }
  }
}

Object.keys(defaultData).forEach((key) => {
  registerAnalyticsEventHandler(key, trackLiveChat);
});
